import { UserStatus } from "@/models/user-status.model";
import { showAlertCallout } from "@/shared/alerts";
import { useUserRepository } from "@/shared/contexts/repository-context";
import { getGetUserListPaginatedQueryKey } from "@/spz-api/user/user";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export const useUpdateUserStatus = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userRepository = useUserRepository();

  const { mutate, isPending } = useMutation<
    void,
    Error,
    { userId: string; status: UserStatus }
  >({
    mutationFn: async ({ userId, status }) => {
      if (status === UserStatus.CONFIRMED) {
        return userRepository.activateUser(userId);
      }
      if (status === UserStatus.DEACTIVATED) {
        return userRepository.deactivateUser(userId);
      }
      if (status === UserStatus.AWAITING) {
        throw new Error("Cannot update user status to awaiting");
      }
      throw new Error(`Invalid status: ${status.value}`);
    },
    onSuccess: (_, variables) => {
      const statusLabelKey =
        variables.status === UserStatus.CONFIRMED
          ? "user_activated_successfully"
          : "user_deactivated_successfully";
      showAlertCallout({
        type: "success",
        title: t(statusLabelKey),
      });
      queryClient.invalidateQueries({
        queryKey: getGetUserListPaginatedQueryKey({}),
      });
    },
    onError: (_, variables) => {
      const errorLabelKey =
        variables.status === UserStatus.CONFIRMED
          ? "error_activating_user"
          : "error_deactivating_user";
      showAlertCallout({
        type: "critical",
        title: t(errorLabelKey),
      });
    },
  });

  return { updateUserStatus: mutate, isPending };
};
